import api from './api';

export const integrationsService = {
  // Get all integrations with optional filters
  getAllIntegrations: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.partner_id) params.append('partner_id', filters.partner_id);
    if (filters.account_id) params.append('account_id', filters.account_id);
    if (filters.hotel_id) params.append('hotel_id', filters.hotel_id);
    if (filters.implementation_hotel_id) params.append('implementation_hotel_id', filters.implementation_hotel_id);
    if (filters.mcp_source_system_id) params.append('mcp_source_system_id', filters.mcp_source_system_id);
    if (filters.accountid) params.append('accountid', filters.accountid);
    if (filters.from_connector_id) params.append('from_connector_id', filters.from_connector_id);
    if (filters.to_connector_id) params.append('to_connector_id', filters.to_connector_id);
    if (filters.name) params.append('name', filters.name);
    if (filters.limit) params.append('limit', filters.limit);

    const queryString = params.toString();
    const url = queryString ? `/pipelines?${queryString}` : '/pipelines';

    const response = await api.get(url);
    return response.data;
  },

  // Get integration by ID
  getIntegrationById: async (id) => {
    const response = await api.get(`/pipelines/${id}`);
    return response.data;
  },

  // Create new integration
  createIntegration: async (integrationData) => {
    const response = await api.post('/pipelines', integrationData);
    return response.data;
  },

  // Update integration
  updateIntegration: async (id, integrationData) => {
    const response = await api.put(`/pipelines/${id}`, integrationData);
    return response.data;
  },

  // Partially update integration
  patchIntegration: async (id, integrationData) => {
    const response = await api.patch(`/pipelines/${id}`, integrationData);
    return response.data;
  },

  // Delete integration
  deleteIntegration: async (id) => {
    const response = await api.delete(`/pipelines/${id}`);
    return response.data;
  },

  // THE DESIGNER'S CANVAS — the nodes and arrows of one integration, read and written whole. A canvas
  // is one picture; sending half of it would mean the server deciding which half to keep. The palette
  // of systems comes back with it, so the designer can never offer one the server would reject.
  getGraph: async (id) => (await api.get(`/pipelines/${id}/graph`)).data,
  // The palette on its own, so a design can be drawn before the integration is saved.
  listDesignerSystems: async () => (await api.get('/pipelines/systems')).data,
  // THE GRAPHS AN IMPLEMENTATION BOARD IS DRAWN FROM, by template id -> graph (null where one could not be read). The
  // boxes are drawn from the designer's system list; a graph read without one borrows it, as the editor does.
  getBoardGraphs: async (templateIds) => {
    const read = await Promise.all([...new Set(templateIds)].map(id =>
      integrationsService.getGraph(id).then(g => [id, g]).catch(() => [id, null])));
    const needSystems = read.some(([, g]) => g && !(g.systems || []).length);
    const systems = needSystems ? ((await integrationsService.listDesignerSystems().catch(() => ({}))).systems || []) : [];
    return Object.fromEntries(read.map(([id, g]) => [id, g && ((g.systems || []).length ? g : { ...g, systems })]));
  },
  saveGraph: async (id, graph) => (await api.put(`/pipelines/${id}/graph`, graph)).data,

  // CUSTOM CODE ON THIS INTEGRATION'S ROUTES (V478). A snippet belongs to the integration and names the
  // routes it runs on (routeIds). Created inactive by the server whatever is sent; switching it on is a
  // separate save.
  listCustomCode: async (id) => (await api.get(`/pipelines/${id}/customcode`)).data,
  getCustomCode: async (id, codeId) => (await api.get(`/pipelines/${id}/customcode/${codeId}`)).data,
  createCustomCode: async (id, snippet) => (await api.post(`/pipelines/${id}/customcode`, snippet)).data,
  // Partial: only the fields sent are written.
  updateCustomCode: async (id, codeId, patch) => (await api.put(`/pipelines/${id}/customcode/${codeId}`, patch)).data,
  deleteCustomCode: async (id, codeId) => (await api.delete(`/pipelines/${id}/customcode/${codeId}`)).data,
  // Claude assistant for a route snippet — an action on the existing POST path.
  // messages: [{ role, content }], snippetContext: { code, modules, sourceSystem, … } -> { reply }
  assistCustomCode: async (id, messages, snippetContext) =>
    (await api.post(`/pipelines/${id}/customcode`, { action: 'assist', messages, snippetContext })).data,

  // A test message through the integration's own OXI endpoint, sent server-side with the source's key.
  // { nodeId, direction: 'push'|'poll', messageType, propertyName, transactionId, message }
  // REAL MESSAGES A SOURCE HAS RECEIVED — what the Test button offers beside its built-in samples.
  // `step` is the inbound step of that wire ('oxi.push.received', 'pegasus.push.received').
  testMessages: async (id, { nodeId, step, limit } = {}) => {
    const qs = new URLSearchParams({ nodeId: String(nodeId) });
    if (step) qs.set('step', step);
    if (limit) qs.set('limit', String(limit));
    return (await api.get(`/pipelines/${id}/test-messages?${qs}`)).data;
  },

  testOxi: async (id, request) => (await api.post(`/pipelines/${id}/test-oxi`, request)).data,
  // Pegasus pushes HTNG/OTA SOAP and is never polled, so there is no direction to choose — the message
  // itself carries the type, the property and the credentials the proxy checks.
  testPegasus: async (id, request) => (await api.post(`/pipelines/${id}/test-pegasus`, request)).data,

  // An OHIP record handed to the proxy as the poller would hand it in — see testohip.js for why
  // this one bypasses the poll rather than imitating it.
  testOhip: async (id, request) => (await api.post(`/pipelines/${id}/test-ohip`, request)).data,

  // THE MCP SOURCE'S OWN SUITE. An mcp-proxy is reached by a client that discovers it, is
  // challenged, signs in and then calls tools, so its test is a sequence of protocol steps rather
  // than one message — see src/integrations/testmcp.js.
  testMcp: async (id, request) => (await api.post(`/pipelines/${id}/test-mcp`, request)).data,
  // The Crawler's dry run — { implementationId, nodeId } → { ok, steps: [{ name, ok, detail }] }.
  testCrawler: async (id, request) => (await api.post(`/pipelines/${id}/test-crawler`, request)).data,
  // Run now (V658): start a source the hub runs on a schedule (the Crawler — one crawl). { implementationId, nodeId }
  runSourceNow: async (id, request) => (await api.post(`/pipelines/${id}/run-now`, request)).data,

  // IMPLEMENTATIONS (V508) — a template is a shape; an implementation is that shape at one property,
  // with that property's credentials. The slots come back with the list because they are derived from
  // the design, so the wizard always asks for exactly what the template currently needs.
  getImplementations: async (id) => (await api.get(`/pipelines/${id}/implementations`)).data,
  // THE HOTELS RUNNING PIPELINES — every template's implementations at the hotels in scope (Pipelines › Hotels).
  // filters: { partner_id, account_id, hotel_id } -> { implementations }
  listHotelImplementations: async (filters = {}) => {
    const params = new URLSearchParams();
    for (const k of ['partner_id', 'account_id', 'hotel_id']) if (filters[k]) params.append(k, filters[k]);
    const qs = params.toString();
    return (await api.get(`/pipelines/implementations${qs ? `?${qs}` : ''}`)).data;
  },
  // ONE implementation, WITH its bindings — what the list deliberately leaves out. The list answers
  // "which properties run this and how far along are they"; this answers "what is actually plugged in",
  // which is only asked of one at a time and is what the board is drawn from.
  getImplementation: async (id, implementationId) =>
    (await api.get(`/pipelines/${id}/implementations/${implementationId}`)).data,
  // Draft / active / paused ONLY — the full save replaces every binding, so it is not used for this.
  setImplementationStatus: async (id, implementationId, status) =>
    (await api.put(`/pipelines/${id}/implementations/${implementationId}/status`, { status })).data,
  // THE BOARD'S GENERATE / REGENERATE of the implementation's Universal API credential: 'generate' issues a new one
  // (the board then makes it the caller), 'regenerate' gives the current one a new client id + secret.
  issueImplementationUapiCredential: async (id, implementationId, mode) =>
    (await api.post(`/pipelines/${id}/implementations/${implementationId}/uapi-credential`, { mode })).data,
  // MOVE ONE PROPERTY TO A PUBLISHED VERSION (V672) — each implementation routes with the version it is on.
  setImplementationVersion: async (id, implementationId, versionId) =>
    (await api.put(`/pipelines/${id}/implementations/${implementationId}/version`, { versionId })).data,
  // THE TEMPLATE'S STREAMING CLIENTS (V617; on the template since V686) — each hotel's client as deployed now, and
  // Deploy (body { logLevel?, implementationIds? } — the hotels named, else every active one). { template, hotels }.
  getStreamingClients: async (id) => (await api.get(`/pipelines/${id}/streaming-clients`)).data,
  deployStreamingClients: async (id, body) => (await api.post(`/pipelines/${id}/streaming-clients`, body || {})).data,
  // THE TEMPLATE'S EVENT POLLER (V635; on the template since V686) — its settings, and each hotel's readiness and last
  // tick; PUT creates or changes the settings (superadmin, audited). { template, poller, hotels, cache } either way.
  getEventPoller: async (id) => (await api.get(`/pipelines/${id}/event-poller`)).data,
  saveEventPoller: async (id, body) => (await api.put(`/pipelines/${id}/event-poller`, body || {})).data,
  // What the poller did with each business event (its steps in the pipeline log) — readable by anyone who can see the
  // template, scoped to their hotels. Same answer shapes as listLogs / getLogGroup.
  listEventPollerProcessed: async (id, params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    return (await api.get(`/pipelines/${id}/event-poller/processed${qs.toString() ? `?${qs}` : ''}`)).data;
  },
  // Poll now (superadmin) — the hotels named, else every active one; queued, answered 202 { queued: [{ id, name }] }.
  pollEventsNow: async (id, implementationIds) =>
    (await api.post(`/pipelines/${id}/event-poller/poll-now`, implementationIds ? { implementationIds } : {})).data,
  getEventPollerProcessedGroup: async (id, groupId) =>
    (await api.get(`/pipelines/${id}/event-poller/processed?groupId=${encodeURIComponent(groupId)}`)).data,
  // A source's PIPELINES settings for one implementation (V654) — the adapter's Config button.
  getSourceConfig: async (id, implementationId, nodeId) =>
    (await api.get(`/pipelines/${id}/implementations/${implementationId}/nodes/${nodeId}/config`)).data,
  saveSourceConfig: async (id, implementationId, nodeId, values) =>
    (await api.put(`/pipelines/${id}/implementations/${implementationId}/nodes/${nodeId}/config`, { values })).data,
  saveImplementation: async (id, body) =>
    (await api.post(`/pipelines/${id}/implementations`, body)).data,
  deleteImplementation: async (id, implementationId) =>
    (await api.delete(`/pipelines/${id}/implementations/${implementationId}`)).data,

  // WHAT IS WAITING FOR A POLLED TARGET (V516) — a target that is never called, so the queue is the
  // only place "did it arrive?" can be answered. `accessId` is the route's binding on one implementation.
  getQueue: async (id, accessId) =>
    (await api.get(`/pipelines/${id}/queue?accessId=${encodeURIComponent(accessId)}`)).data,

  // A target that STORES rather than answers (V583 result viewer): one folder of it, or — with { name } — one
  // file's content. Read through the binding's own credential (pipelines files.js).
  getFiles: async (id, accessId, { prefix, marker, name } = {}) => {
    const params = new URLSearchParams({ accessId: String(accessId) });
    if (prefix) params.set('prefix', prefix);
    if (marker) params.set('marker', marker);
    if (name) params.set('name', name);
    return (await api.get(`/pipelines/${id}/files?${params.toString()}`)).data;
  },

  // ── VERSIONS (V512) ───────────────────────────────────────────────────────
  //
  // The DRAFT is the design itself — what the designer edits and what Save Changes writes. These are
  // the frozen snapshots and the pointer at whichever one is running. Publishing and going live are
  // separate calls because they are separate decisions: a version can be frozen and read before
  // anything runs it, and a rollback is then a pointer move rather than an edit under an outage.
  listVersions: async (id) => (await api.get(`/pipelines/${id}/versions`)).data,
  getVersion: async (id, versionId) => (await api.get(`/pipelines/${id}/versions/${versionId}`)).data,
  publishVersion: async (id, notes) => (await api.post(`/pipelines/${id}/versions`, { notes })).data,
  makeVersionLive: async (id, versionId) =>
    (await api.put(`/pipelines/${id}/versions/${versionId}/live`)).data,
  takeVersionOffline: async (id, versionId) =>
    (await api.delete(`/pipelines/${id}/versions/${versionId}/live`)).data,
  // Replaces the DRAFT with a published version's design. Not the same as going live — this changes
  // what is being edited and leaves what is running alone.
  restoreVersion: async (id, versionId) =>
    (await api.post(`/pipelines/${id}/versions/${versionId}/restore`)).data,
  deleteVersion: async (id, versionId) =>
    (await api.delete(`/pipelines/${id}/versions/${versionId}`)).data,

  // THE INTEGRATION LOG (integration-log-*) — one record per message, grouped on the server, newest first;
  // superadmin only. params: { integrationId, transactionId, step, outcome, messageType, q, since, until,
  // limit, offset } -> { groups, nextOffset }
  listLogs: async (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
    return (await api.get(`/pipelines/logs${qs.toString() ? `?${qs}` : ''}`)).data;
  },
  // What the Logs tab's Hotel / Source / Module filters can be set to, under the other filters in force.
  // -> { hotels: [{ id, property, name }], systems: [], modules: [] }
  getLogFacets: async (params = {}) => {
    const qs = new URLSearchParams({ facets: '1' });
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    return (await api.get(`/pipelines/logs?${qs}`)).data;
  },
  // One record's steps, oldest first, with details and bodies — fetched when the record is opened.
  getLogGroup: async (groupId) =>
    (await api.get(`/pipelines/logs?groupId=${encodeURIComponent(groupId)}`)).data,
  // Raise a Jira for ONE record. The server re-reads the record and builds the ticket, because the Jira
  // credentials are in the lambda's secret and the ticket carries the detail and bodies the list does not.
  // -> { key, id, url }
  raiseLogJira: async (payload) => (await api.post('/pipelines/logs/jira', payload)).data,
  // Ask AI about ONE record — the server re-reads it and gives Claude the steps, bodies, credential and routes.
  // messages: [{ role, content }], logContext: { groupId } -> { reply }
  explainLog: async (messages, logContext) =>
    (await api.post('/pipelines/logs/explain', { messages, groupId: logContext && logContext.groupId })).data,
  // Send one logged message through its proxy again, from the original as received (logs.js resend).
  resendLogMessage: async (groupId) => (await api.post('/pipelines/logs/resend', { groupId })).data,
  // Delete the entries matching the same filters as listLogs (no filters = the whole log). -> { deleted }
  purgeLogs: async (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
    return (await api.delete(`/pipelines/logs${qs.toString() ? `?${qs}` : ''}`)).data;
  },
};

export default integrationsService;
